import React from 'react';
import { CreditCard, Package, Truck, CheckCircle, ExternalLink, MapPin, Clock } from 'lucide-react';

const TRACKING_LINKS = {
    'CJ대한통운': 'https://trace.cjlogistics.com/web/detail.jsp?slipno=',
    '한진택배': 'https://www.hanjin.com/kor/CMS/DeliveryMgr/WaybillResult.do?mession_key=&wblnum=',
    '롯데택배': 'https://www.lotteglogis.com/home/reservation/tracking/link498',
    '로젠택배': 'https://www.ilogen.com/web/personal/trace/',
    '우체국택배': 'https://service.epost.go.kr/trace.RetrieveDomRi498.comm?sid1=',
    '경동택배': 'https://kdexp.com/basicNew498.kd?barcode=',
    '대신택배': 'https://www.ds3211.co.kr/freight/internalFreightSearch.do?billNo='
};

const STATUS_STEPS = [
    { key: 'placed', label: '주문접수', icon: CreditCard },
    { key: 'confirmed', label: '주문확인', icon: Package },
    { key: 'shipped', label: '배송중', icon: Truck },
    { key: 'delivered', label: '수령완료', icon: CheckCircle }
];

function OrderTracking({ order }) {
    if (!order) {
        return (
            <div style={styles.emptyContainer}>
                <Package size={48} color="#cbd5e1" />
                <p style={styles.emptyText}>주문 정보가 없습니다</p>
            </div>
        );
    }

    const getStatusIndex = () => {
        const statusMap = {
            'placed': 0,
            'pending': 0,
            'paid': 0,
            'payment_completed': 0,
            'confirmed': 1,
            'preparing': 1,
            'shipping_ready': 1,
            'shipped': 2,
            'shipping': 2,
            'in_transit': 2,
            'delivered': 3,
            'completed': 3
        };
        return statusMap[order.status] ?? 0;
    };

    const currentIndex = getStatusIndex();

    const getStepTimestamp = (stepKey) => {
        // Check statusHistory array
        if (order.statusHistory && Array.isArray(order.statusHistory)) {
            const entry = order.statusHistory.find(h =>
                h.status === stepKey ||
                (stepKey === 'paid' && (h.status === 'paid' || h.status === 'payment_completed')) ||
                (stepKey === 'preparing' && (h.status === 'preparing' || h.status === 'shipping_ready')) ||
                (stepKey === 'shipping' && (h.status === 'shipped' || h.status === 'shipping' || h.status === 'in_transit')) ||
                (stepKey === 'delivered' && (h.status === 'delivered' || h.status === 'completed'))
            );
            if (entry?.timestamp) {
                const ts = entry.timestamp;
                return ts.toDate ? ts.toDate() : new Date(ts);
            }
        }

        // Fallback to individual timestamp fields
        const fieldMap = {
            'placed': order.createdAt || order.paidAt || order.orderDate,
            'confirmed': order.confirmedAt,
            'shipped': order.shippedAt || order.shippingAt,
            'delivered': order.deliveredAt || order.completedAt
        };

        const val = fieldMap[stepKey];
        if (!val) return null;
        return val.toDate ? val.toDate() : new Date(val);
    };

    const formatTimestamp = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('ko-KR', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const carrier = order.shipping?.carrier || order.carrier || '';
    const trackingNumber = order.shipping?.trackingNumber || order.trackingNumber || '';
    const trackingBaseUrl = TRACKING_LINKS[carrier] || '';
    const trackingUrl = trackingBaseUrl ? `${trackingBaseUrl}${trackingNumber}` : '';

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <Truck size={24} color="#6366f1" />
                    <h3 style={styles.headerTitle}>배송 추적</h3>
                </div>
                {order.orderId && (
                    <span style={styles.orderId}>주문번호: {order.orderId || order.id}</span>
                )}
            </div>

            {/* Timeline */}
            <div style={styles.timeline}>
                {STATUS_STEPS.map((step, index) => {
                    const IconComponent = step.icon;
                    const isCompleted = index < currentIndex;
                    const isCurrent = index === currentIndex;
                    const isFuture = index > currentIndex;
                    const timestamp = getStepTimestamp(step.key);

                    return (
                        <div key={step.key} style={styles.timelineStep}>
                            {/* Connector line */}
                            {index > 0 && (
                                <div style={{
                                    ...styles.connectorLine,
                                    backgroundColor: isCompleted || isCurrent ? '#6366f1' : '#e2e8f0'
                                }} />
                            )}

                            {/* Dot */}
                            <div style={{
                                ...styles.dotOuter,
                                ...(isCompleted ? styles.dotCompleted : {}),
                                ...(isCurrent ? styles.dotCurrent : {}),
                                ...(isFuture ? styles.dotFuture : {})
                            }}>
                                <IconComponent
                                    size={20}
                                    color={isCompleted ? '#ffffff' : isCurrent ? '#6366f1' : '#cbd5e1'}
                                />
                                {isCurrent && <div style={styles.pulseRing} />}
                            </div>

                            {/* Label */}
                            <div style={styles.stepContent}>
                                <span style={{
                                    ...styles.stepLabel,
                                    color: isCompleted || isCurrent ? '#0f172a' : '#94a3b8',
                                    fontWeight: isCurrent ? '700' : '600'
                                }}>
                                    {step.label}
                                </span>
                                {timestamp && (
                                    <span style={styles.stepTimestamp}>
                                        <Clock size={12} />
                                        {formatTimestamp(timestamp)}
                                    </span>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Shipping Info */}
            {(currentIndex >= 2 && (carrier || trackingNumber)) && (
                <div style={styles.shippingInfo}>
                    <div style={styles.shippingRow}>
                        <span style={styles.shippingLabel}>택배사</span>
                        <span style={styles.shippingValue}>{carrier || '-'}</span>
                    </div>
                    <div style={styles.shippingRow}>
                        <span style={styles.shippingLabel}>송장번호</span>
                        <span style={styles.shippingValue}>{trackingNumber || '-'}</span>
                    </div>
                    {trackingUrl && (
                        <a
                            href={trackingUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={styles.trackingLink}
                        >
                            <MapPin size={16} />
                            <span>배송 조회하기</span>
                            <ExternalLink size={14} />
                        </a>
                    )}
                </div>
            )}

            <style>{`
                @keyframes pulse {
                    0% { transform: scale(1); opacity: 0.8; }
                    50% { transform: scale(1.8); opacity: 0; }
                    100% { transform: scale(1); opacity: 0; }
                }
            `}</style>
        </div>
    );
}

const styles = {
    container: {
        backgroundColor: '#ffffff',
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
    },
    emptyContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px'
    },
    emptyText: {
        marginTop: '12px',
        fontSize: '15px',
        color: '#64748b'
    },
    header: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '28px',
        paddingBottom: '16px',
        borderBottom: '2px solid #f1f5f9',
        flexWrap: 'wrap',
        gap: '12px'
    },
    headerLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px'
    },
    headerTitle: {
        margin: 0,
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a'
    },
    orderId: {
        fontSize: '13px',
        color: '#94a3b8',
        fontWeight: '500'
    },

    // Timeline
    timeline: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0',
        position: 'relative',
        paddingLeft: '24px'
    },
    timelineStep: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '16px',
        position: 'relative',
        paddingBottom: '32px'
    },
    connectorLine: {
        position: 'absolute',
        left: '19px',
        top: '-16px',
        width: '2px',
        height: '32px',
        marginLeft: '-24px'
    },
    dotOuter: {
        width: '42px',
        height: '42px',
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        marginLeft: '-24px',
        zIndex: 1
    },
    dotCompleted: {
        backgroundColor: '#10b981',
        boxShadow: '0 2px 8px rgba(16, 185, 129, 0.3)'
    },
    dotCurrent: {
        backgroundColor: '#eef2ff',
        border: '3px solid #6366f1',
        boxShadow: '0 2px 8px rgba(99, 102, 241, 0.3)'
    },
    dotFuture: {
        backgroundColor: '#f1f5f9',
        border: '2px solid #e2e8f0'
    },
    pulseRing: {
        position: 'absolute',
        top: '-3px',
        left: '-3px',
        right: '-3px',
        bottom: '-3px',
        borderRadius: '50%',
        border: '2px solid #6366f1',
        animation: 'pulse 2s ease-in-out infinite'
    },
    stepContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        paddingTop: '8px'
    },
    stepLabel: {
        fontSize: '15px',
        fontWeight: '600'
    },
    stepTimestamp: {
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
        fontSize: '12px',
        color: '#94a3b8'
    },

    // Shipping Info
    shippingInfo: {
        marginTop: '8px',
        padding: '20px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
    },
    shippingRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '8px 0',
        borderBottom: '1px solid #f1f5f9'
    },
    shippingLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b'
    },
    shippingValue: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#0f172a'
    },
    trackingLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        marginTop: '16px',
        padding: '12px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: '1px solid #c7d2fe',
        borderRadius: '10px',
        textDecoration: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
        width: 'fit-content'
    }
};

export default OrderTracking;
